import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, Eye, FileText, UserCheck } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const { empresaId } = useParams<{ empresaId?: string }>();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-gray-200 p-6 md:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Botão Voltar */}
        <Link
          to={empresaId ? `/login/${empresaId}` : "/login"}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 transition-colors mb-8 group"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          Voltar para o Login
        </Link>

        {/* Header */}
        <div className="bg-gray-800/50 backdrop-blur-md rounded-3xl p-8 border border-gray-700/50 mb-12 shadow-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-3">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight">Política de Privacidade</h1>
              <p className="text-orange-400 font-medium">Sua segurança e privacidade são nossa prioridade</p>
            </div>
          </div>
          <p className="text-gray-400 leading-relaxed max-w-2xl">
            Bem-vindo à nossa Política de Privacidade. Este documento explica como coletamos, utilizamos, protegemos e compartilhamos suas informações pessoais ao utilizar nosso sistema e aplicativo.
          </p>
        </div>

        {/* Conteúdo */}
        <div className="space-y-8 pb-12">
          {/* Seção 1 */}
          <section className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700/30 hover:border-orange-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-6 w-6 text-orange-500" />
              <h2 className="text-xl font-bold text-white">1. Informações que Coletamos</h2>
            </div>
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>
                Para fornecer a melhor experiência possível, coletamos as seguintes informações:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><span className="text-gray-200">Dados de Cadastro:</span> Nome, e-mail, telefone e endereço (quando necessário para entregas).</li>
                <li><span className="text-gray-200">Dados de Pedidos:</span> Histórico de compras, itens selecionados e preferências de consumo.</li>
                <li><span className="text-gray-200">Dados de Dispositivo:</span> Endereço IP, tipo de navegador e informações básicas do sistema operacional para fins de segurança.</li>
              </ul>
            </div>
          </section>

          {/* Seção 2 */}
          <section className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700/30 hover:border-orange-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="h-6 w-6 text-orange-500" />
              <h2 className="text-xl font-bold text-white">2. Como Usamos Seus Dados</h2>
            </div>
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>Seus dados são utilizados exclusivamente para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Processar e gerenciar seus pedidos em tempo real.</li>
                <li>Personalizar sua experiência baseada em suas preferências anteriores.</li>
                <li>Garantir a segurança da sua conta e prevenir fraudes.</li>
                <li>Comunicação sobre o status dos seus pedidos e atualizações importantes do sistema.</li>
              </ul>
            </div>
          </section>

          {/* Seção 3 */}
          <section className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700/30 hover:border-orange-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-6 w-6 text-orange-500" />
              <h2 className="text-xl font-bold text-white">3. Segurança dos Dados</h2>
            </div>
            <div className="text-gray-400 leading-relaxed">
              <p>
                Implementamos medidas de segurança técnicas e organizacionais rigorosas para proteger seus dados contra acesso não autorizado, alteração, divulgação ou destruição. Utilizamos criptografia e protocolos de segurança modernos (SSL/TLS) para todas as transações de dados.
              </p>
            </div>
          </section>

          {/* Seção 4 */}
          <section className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700/30 hover:border-orange-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="h-6 w-6 text-orange-500" />
              <h2 className="text-xl font-bold text-white">4. Seus Direitos</h2>
            </div>
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>Você tem o direito de:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Acessar e atualizar suas informações pessoais a qualquer momento através do seu perfil.</li>
                <li>Solicitar a exclusão total de seus dados de nossa base de dados.</li>
                <li>Retirar seu consentimento para o processamento de dados específicos.</li>
              </ul>
            </div>
          </section>

          {/* Rodapé Interno */}
          <div className="text-center pt-8 border-t border-gray-700/30">
            <p className="text-gray-500 text-sm mb-4">
              Última atualização: {new Date().toLocaleDateString('pt-BR')}
            </p>
            <p className="text-gray-400">
              Se você tiver dúvidas sobre esta política, entre em contato com nosso suporte técnico.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #111822;
        }
        ::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicyPage;
